import { LayoutChangeEvent } from "react-native";
import { useAnimatedStyle, useSharedValue, withDelay, withTiming } from "react-native-reanimated";
import { Easing } from "react-native-reanimated";

interface useAnimatedProgressBarParams {
  index: number;
  activeColor: string;
  inactiveColor: string;
  isActive: boolean;
}

export const useAnimatedProgressBar = (params: useAnimatedProgressBarParams) => {
  const { index, activeColor, inactiveColor, isActive } = params;
  const isFirst = index === 0;

  const dotStyle = useSharedValue({
    size: 8,
    backgroundColor: inactiveColor,
  });
  const barHeight = useSharedValue(0);

  const dotDuration = 250;
  const lineDuration = dotDuration / 2;
  const firstDuration = 100 + dotDuration + lineDuration;
  const fullDuration = dotDuration + (lineDuration * 2);
  const totalDelay = fullDuration * (index - 1) + firstDuration;

  const barTopDelay = isFirst ? 0 : totalDelay;
  const dotDelay = isFirst ? 100 : totalDelay + lineDuration;
  const barBottomDelay = isFirst ? 100 + dotDuration : totalDelay + lineDuration + dotDuration;

  const animatedDotStyle = useAnimatedStyle(() => ({
    height: dotStyle.value.size,
    width: dotStyle.value.size,
    backgroundColor: dotStyle.value.backgroundColor,
  }));

  const animatedBarTopStyle = useAnimatedStyle(() => ({
    height: withDelay(
      barTopDelay,
      withTiming(barHeight.value, { duration: lineDuration, easing: Easing.sin })
    ),
  }));

  const animatedBarBottomStyle = useAnimatedStyle(() => ({
    height: withDelay(
      barBottomDelay,
      withTiming(barHeight.value, { duration: lineDuration, easing: Easing.sin })
    ),
  }));

  function onBarLayoutChange(event: LayoutChangeEvent) {
    barHeight.value = event.nativeEvent.layout.height;
  }

  if (isActive) {
    dotStyle.value = {
      size: withDelay(dotDelay, withTiming(16, { duration: dotDuration, easing: Easing.bounce })),
      backgroundColor: withDelay(dotDelay, withTiming(activeColor, { duration: dotDuration })),
    };
  } else {
    dotStyle.value = {
      size: 8,
      backgroundColor: inactiveColor,
    };
  }

  return {
    animatedDotStyle,
    animatedBarTopStyle,
    animatedBarBottomStyle,
    onBarLayoutChange,
  }
}